import { useMemo, useCallback } from 'react';
import { differenceInDays } from 'date-fns';
import { useIntl } from 'react-intl';
import { DateRangePicker } from './DateRangePicker';
import type { DateValue, PresetOption } from './types';
import type { DailyPresetId, DailyComparisonPresetId } from './presets/types';
import {
  DAILY_PRESET_REGISTRY,
  DAILY_COMPARISON_REGISTRY,
  buildPresetOptions,
  buildComparisonOptions,
  recalcComparison,
} from './presets';
import { isValidDateRange, isDateDisabled, isValidComparisonRange } from './validation';

export interface DailyDateRangePickerProps {
  isComparison?: boolean;
  caption: string;
  value: DateValue;
  onChange: (value: DateValue) => void;
  presetIds: DailyPresetId[] | DailyComparisonPresetId[];
  comparisonPresetIds?: DailyComparisonPresetId[];
  disabled?: boolean;
  emptyMessage?: string;
  hideButtons?: boolean;
  hideDateInputs?: boolean;
  fullWidth?: boolean;
}

export function DailyDateRangePicker({
  value,
  onChange,
  presetIds,
  caption,
  comparisonPresetIds = undefined,
  isComparison = false,
  disabled = false,
  emptyMessage = 'datepicker.empty.default',
  hideButtons = true,
  hideDateInputs = undefined,
  fullWidth = false,
}: DailyDateRangePickerProps) {
  const intl = useIntl();
  const today = useMemo(() => new Date(), []);

  const presetOptions = useMemo<PresetOption[]>(() => {
    if (isComparison) {
      return buildComparisonOptions(
        presetIds as DailyComparisonPresetId[],
        DAILY_COMPARISON_REGISTRY,
        today,
        value.range
      );
    }
    return buildPresetOptions(presetIds as DailyPresetId[], DAILY_PRESET_REGISTRY, today);
  }, [isComparison, presetIds, today, value.range]);

  const fieldValue: DateValue = useMemo(() => {
    if (isComparison) {
      return {
        range: value.comparison ?? { presetValue: 'no_comparison' },
        comparison: undefined,
      };
    }
    return { ...value, comparison: undefined };
  }, [isComparison, value]);

  const mainRangeDuration = useMemo(() => {
    if (isComparison && value.range.from && value.range.to) {
      return differenceInDays(value.range.to, value.range.from);
    }
    return undefined;
  }, [isComparison, value.range.from, value.range.to]);

  const handleChange = useCallback(
    (newValue: DateValue) => {
      if (isComparison) {
        const range = newValue.range;
        const validRange = isValidComparisonRange(range) ? range : { presetValue: 'no_comparison' };
        onChange({ ...value, comparison: validRange });
      } else if (comparisonPresetIds && comparisonPresetIds.length > 0) {
        const newComparison = recalcComparison(value, newValue);
        const validComparison = isValidComparisonRange(newComparison ?? {})
          ? newComparison
          : { presetValue: 'no_comparison' };
        onChange({ range: newValue.range, comparison: validComparison });
      } else {
        onChange({ range: newValue.range });
      }
    },
    [isComparison, value, onChange, comparisonPresetIds]
  );

  const effectiveHideDateInputs = hideDateInputs ?? !presetOptions.some(p => p.value === 'custom');

  return (
    <DateRangePicker
      granularity="daily"
      value={fieldValue}
      onChange={handleChange}
      presetOptions={presetOptions}
      caption={caption}
      emptyMessage={emptyMessage}
      disabled={disabled}
      disabledDates={isDateDisabled}
      onValidate={(startDate, endDate) => isValidDateRange(startDate, endDate, false, intl)}
      isComparison={isComparison}
      mainRangeDuration={mainRangeDuration}
      hideButtons={hideButtons}
      hideDateInputs={effectiveHideDateInputs}
      fullWidth={fullWidth}
    />
  );
}
